import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import AppLayout from './components/AppLayout';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import HomePage from './pages/HomePage';
import MoodPage from './pages/MoodPage';
import ProgramsPage from './pages/ProgramsPage';
import GratitudePage from './pages/GratitudePage';
import PuzzlesPage from './pages/PuzzlesPage';
import PersonalityPage from './pages/PersonalityPage';
import PremiumPage from './pages/PremiumPage';
import WeeklyPlanPage from './pages/WeeklyPlanPage';
import ProfilePage from './pages/ProfilePage';
import OnboardingPage from './pages/OnboardingPage';
import DoodlePage from './pages/DoodlePage';
import GoogleAuthCallback from './pages/GoogleAuthCallback';
import AboutPage from './pages/AboutPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsPage from './pages/TermsPage';
import ContactPage from './pages/ContactPage';
import { trackPageView, setAnalyticsUserId } from './lib/firebase';
import { fbPageView } from './lib/fbpixel';
import { capturePromoFromUrl } from './lib/promo';
import './App.css';

const APP_NAME = 'Today: Daily Reset';

// Friendly titles + GA4 screen_name mapping for each route
const ROUTE_META = [
  { match: /^\/login/,              title: 'Sign In',             screen: 'login' },
  { match: /^\/register/,           title: 'Create Account',      screen: 'register' },
  { match: /^\/onboarding/,         title: 'Onboarding',          screen: 'onboarding' },
  { match: /^\/mood/,               title: 'Mood Tracker',        screen: 'mood' },
  { match: /^\/programs/,           title: 'Wellness Programs',   screen: 'programs' },
  { match: /^\/gratitude/,          title: 'Gratitude Journal',   screen: 'gratitude' },
  { match: /^\/puzzles/,            title: 'Daily Puzzles',       screen: 'puzzles' },
  { match: /^\/doodle/,             title: 'Mindful Doodle',      screen: 'doodle' },
  { match: /^\/about/,              title: 'About',               screen: 'about' },
  { match: /^\/privacy-policy/,     title: 'Privacy Policy',      screen: 'privacy_policy' },
  { match: /^\/terms/,              title: 'Terms & Conditions',  screen: 'terms' },
  { match: /^\/contact/,            title: 'Contact',             screen: 'contact' },
  { match: /^\/personality\/iq/,    title: 'IQ Test',             screen: 'personality_iq' },
  { match: /^\/personality/,        title: 'Personality Test',    screen: 'personality' },
  { match: /^\/premium/,            title: 'Premium',             screen: 'premium' },
  { match: /^\/weekly-plan/,        title: 'Weekly Plan',         screen: 'weekly_plan' },
  { match: /^\/profile/,            title: 'Profile',             screen: 'profile' },
  { match: /^\/$/,                  title: 'Home',                screen: 'home' },
];

function resolveRouteMeta(pathname) {
  const m = ROUTE_META.find((r) => r.match.test(pathname));
  return m ? { title: m.title, screen: m.screen } : { title: 'App', screen: 'unknown' };
}

function AuthRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/" replace />;
  return children;
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AppRouter() {
  const location = useLocation();
  const { deviceId } = useAuth();

  // Track page views on every route change.
  useEffect(() => {
    // Silently capture `?promo=CODE` from URL into localStorage (no UI surface).
    capturePromoFromUrl();
    const { title, screen } = resolveRouteMeta(location.pathname);
    const fullTitle = `${title} · ${APP_NAME}`;
    if (typeof document !== 'undefined') document.title = fullTitle;
    trackPageView(location.pathname + location.search, fullTitle, screen);
    fbPageView();
  }, [location.pathname, location.search]);

  // Attach the device id (stable per-browser identifier) to GA events for funnel analysis.
  useEffect(() => {
    if (deviceId) setAnalyticsUserId(deviceId);
  }, [deviceId]);

  return (
    <Routes>
      <Route path="/login" element={<AuthRoute><LoginPage /></AuthRoute>} />
      <Route path="/register" element={<AuthRoute><RegisterPage /></AuthRoute>} />
      <Route path="/auth/google" element={<GoogleAuthCallback />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
      <Route path="/" element={<ProtectedRoute><AppLayout><HomePage /></AppLayout></ProtectedRoute>} />
      <Route path="/mood" element={<ProtectedRoute><AppLayout><MoodPage /></AppLayout></ProtectedRoute>} />
      <Route path="/programs" element={<ProtectedRoute autoGuest><AppLayout><ProgramsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/gratitude" element={<ProtectedRoute><AppLayout><GratitudePage /></AppLayout></ProtectedRoute>} />
      <Route path="/puzzles" element={<ProtectedRoute><AppLayout><PuzzlesPage /></AppLayout></ProtectedRoute>} />
      <Route path="/doodle" element={<ProtectedRoute><AppLayout><DoodlePage /></AppLayout></ProtectedRoute>} />
      <Route path="/personality" element={<ProtectedRoute autoGuest><AppLayout><PersonalityPage /></AppLayout></ProtectedRoute>} />
      <Route path="/personality/iq" element={<ProtectedRoute autoGuest><AppLayout><PersonalityPage initialTab="iq" /></AppLayout></ProtectedRoute>} />
      <Route path="/premium" element={<ProtectedRoute autoGuest><AppLayout><PremiumPage /></AppLayout></ProtectedRoute>} />
      <Route path="/weekly-plan" element={<ProtectedRoute><AppLayout><WeeklyPlanPage /></AppLayout></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><AppLayout><ProfilePage /></AppLayout></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
